import { AsyncPipe } from "@angular/common";
import { Component, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatDivider } from "@angular/material/divider";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { AuthService } from "@lib/auth/interfaces/IAuthService";
import { NavigationMenuRoutes } from "@lib/base/components/navigation/navigation-menu/navigation-menu.types";
import { UserRolesService } from "@lib/core/services/user-roles.service";
import { TranslatePipe } from "@ngx-translate/core";
import { filter, map, Observable, of, startWith, switchMap, zip, zipWith } from "rxjs";

@Component({
    selector: "app-navigation-menu",
    imports: [
        MatIcon,
        MatMenuTrigger,
        MatMenu,
        MatIconButton,
        MatMenuItem,
        RouterLink,
        TranslatePipe,
        MatDivider,
        AsyncPipe,
        MatButton,
    ],
    templateUrl: "./navigation-menu.component.html",
    styleUrl: "./navigation-menu.component.scss",
})
export class NavigationMenuComponent {
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly rolesService = inject(UserRolesService);

    menuData = input.required<NavigationMenuRoutes[]>();

    route$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => event.url),
        startWith(this.router.url),
    );

    route = toSignal(this.route$);

    isAdminSignal = toSignal(this.rolesService.isAdmin(), { initialValue: false });

    private readonly menuData$: Observable<Array<NavigationMenuRoutes & {
        selected?: boolean;
    }>> = toObservable(this.menuData);

    protected readonly enrichedMenuData$ = this.menuData$.pipe(
        switchMap(menuData => zip(menuData.map((menuItem) => {
            if (menuItem.SuperAdminOnly) {
                return this.rolesService.isAdmin().pipe(
                    map((isAdmin) => {
                        return isAdmin ? menuItem : null;
                    }),
                );
            } else if (menuItem.anyReadPermission != null && menuItem.anyReadPermission.length > 0) {
                return this.rolesService.canIAll("READ", menuItem.anyReadPermission).pipe(
                    map(permissions => permissions.find(value => value.canAccess) != null),
                    map(hasPermission => hasPermission ? menuItem : null),
                );
            } else if (menuItem.allAbsoluteReadPermission != null && menuItem.allAbsoluteReadPermission.length > 0) {
                return this.rolesService.canIAll("READ", menuItem.allAbsoluteReadPermission).pipe(
                    map(permissions => permissions.every(value => value.canAccess)),
                    map(hasPermission => hasPermission ? menuItem : null),
                );
            }

            return of(menuItem);
        }))),
        map(menuItems => menuItems.filter(item => item != null)),
        zipWith(this.route$),
        map(([menuItems, route]) => menuItems.map((menuItem) => {
            return {
                ...menuItem,
                selected: this.isSelected(route, menuItem.route),
            };
        })),
        map((menuItems) => {
            if (menuItems.length === 1 && menuItems[0].hideIfAlone) {
                return [];
            } else {
                return menuItems;
            }
        }),
    );

    isSelected(currentRoute: string | undefined, selectedRoute: string) {
        if (currentRoute === undefined) return false;
        const currentSegment = currentRoute.split("/").filter(e => e !== "")[0];
        const selectedSegment = selectedRoute.split("/").filter(e => e !== "")[0];
        return currentSegment != null && currentSegment === selectedSegment;
    }

    logout() {
        this.authService.logOut();
    }
}
